import React from 'react';
import ArticleShell from './ArticleShell';
import { H2, H3, P, Callout, CheckList, CrossList, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const UAEVATGuide = () => {
  const guide = { ...getGuideBySlug('uae-vat-guide-2026'), relatedService: { label: 'VAT Services', path: '/vat-services-uae' } };

  const toc = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'what-is-vat', label: 'What is VAT?' },
    { id: 'who-must-register', label: 'Who Must Register' },
    { id: 'supply-categories', label: 'Taxable, Zero-Rated & Exempt' },
    { id: 'input-output-vat', label: 'Input & Output VAT' },
    { id: 'residual-vat', label: 'Residual Input VAT' },
    { id: 'imports-exports', label: 'Imports, Exports & RCM' },
    { id: 'returns', label: 'VAT Returns & Compliance' },
    { id: 'mistakes', label: 'Common Mistakes' },
    { id: 'penalties', label: 'Penalties' },
    { id: 'deregistration', label: 'Deregistration' },
    { id: 'wpaa-help', label: 'How WPAA Can Help' }
  ];

  return (
    <ArticleShell guide={guide} toc={toc} testId="ka-article-vat" ctaService="vat">
      <Callout variant="info" title="A note on this guide">
        UAE tax regulations evolve. This guide covers the enduring fundamentals every business needs to know.
        For anything specific to your circumstances, or for the latest FTA rules, please book a free
        consultation with our team.
      </Callout>

      <H2 id="introduction">Introduction</H2>
      <P>
        Value Added Tax (VAT) was introduced in the UAE on 1 January 2018 at a standard rate of 5%. Since then,
        VAT has become an integral part of business operations, affecting sales, purchases, imports, exports
        and compliance obligations.
      </P>
      <P>
        Whether you are a start-up, SME, free-zone company or multinational business, understanding VAT is
        essential to avoid penalties and maximise input tax recovery.
      </P>

      <H2 id="what-is-vat">What is VAT?</H2>
      <P>
        VAT is an <strong>indirect tax</strong> imposed on the consumption of goods and services. Businesses
        collect VAT from customers and remit it to the <strong>Federal Tax Authority (FTA)</strong>. The
        standard VAT rate in the UAE is <strong>5%</strong>.
      </P>

      <H2 id="who-must-register">Who Must Register for VAT?</H2>
      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="border-2 border-rose-100 bg-rose-50/50 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-rose-700 mb-2">Mandatory Registration</p>
          <p className="text-lg font-bold text-gray-900 mb-2">AED 375,000</p>
          <p className="text-sm text-gray-700">Required when taxable supplies and imports <strong>exceed AED 375,000</strong> in the previous 12 months, or are expected to exceed that amount in the next 30 days.</p>
        </div>
        <div className="border-2 border-emerald-100 bg-emerald-50/50 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Voluntary Registration</p>
          <p className="text-lg font-bold text-gray-900 mb-2">AED 187,500</p>
          <p className="text-sm text-gray-700">Available when taxable supplies exceed <strong>AED 187,500</strong>. Often useful for start-ups that want to recover VAT on setup costs.</p>
        </div>
      </div>

      <H2 id="supply-categories">Taxable, Zero-Rated and Exempt Supplies</H2>
      <P>Not every business activity is taxed the same way. Correct classification determines both the VAT you charge and the VAT you can recover.</P>
      <div className="overflow-x-auto my-6 rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-bold text-gray-900">Standard-Rated (5%)</th>
              <th className="text-left px-4 py-3 font-bold text-gray-900 border-l border-gray-200">Zero-Rated (0%)</th>
              <th className="text-left px-4 py-3 font-bold text-gray-900 border-l border-gray-200">Exempt (No VAT)</th>
            </tr>
          </thead>
          <tbody className="align-top text-gray-700">
            <tr className="border-b border-gray-100"><td className="px-4 py-3">Commercial rent</td><td className="px-4 py-3 border-l border-gray-100">Export of goods</td><td className="px-4 py-3 border-l border-gray-100">Residential buildings</td></tr>
            <tr className="border-b border-gray-100"><td className="px-4 py-3">Most professional services</td><td className="px-4 py-3 border-l border-gray-100">Export of services (subject to conditions)</td><td className="px-4 py-3 border-l border-gray-100">Bare land</td></tr>
            <tr className="border-b border-gray-100"><td className="px-4 py-3">Trading activities</td><td className="px-4 py-3 border-l border-gray-100">International transportation</td><td className="px-4 py-3 border-l border-gray-100">Certain financial services</td></tr>
            <tr><td className="px-4 py-3">Consulting services</td><td className="px-4 py-3 border-l border-gray-100">Certain educational &amp; healthcare services</td><td className="px-4 py-3 border-l border-gray-100">Local passenger transport</td></tr>
          </tbody>
          <tfoot className="bg-gray-50 border-t border-gray-200">
            <tr>
              <td className="px-4 py-3 text-xs font-semibold text-emerald-700">Input VAT: recoverable</td>
              <td className="px-4 py-3 text-xs font-semibold text-emerald-700 border-l border-gray-200">Input VAT: recoverable</td>
              <td className="px-4 py-3 text-xs font-semibold text-rose-700 border-l border-gray-200">Input VAT: not recoverable</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <H2 id="input-output-vat">Input VAT and Output VAT</H2>
      <P><strong>Output VAT</strong> is VAT charged on sales. <strong>Input VAT</strong> is VAT paid on purchases. The difference between the two determines whether a business pays VAT to the FTA or is due a refund.</P>

      <H3>Recovering Input VAT</H3>
      <p className="text-gray-700 mb-2">Businesses can generally recover VAT on:</p>
      <CheckList items={['Office rent', 'Utilities', 'Professional fees', 'Inventory purchases', 'Business software', 'Marketing expenses']} />
      <p className="text-gray-700 mb-2">VAT cannot usually be recovered on:</p>
      <CrossList items={['Personal expenses', 'Employee entertainment', 'Certain motor-vehicle expenses (private use)', 'Non-business expenditures']} />

      <H2 id="residual-vat">What is Residual Input VAT?</H2>
      <P>Residual VAT arises when expenses relate to <strong>both taxable and exempt activities</strong>. Common examples include schools, hospitals, banks and real-estate companies. Residual VAT must be apportioned using an FTA-approved recovery method.</P>

      <H2 id="imports-exports">VAT on Imports, Exports and the Reverse Charge Mechanism</H2>
      <P>Imports are generally subject to VAT under the <strong>Reverse Charge Mechanism (RCM)</strong>, where the recipient accounts for the VAT instead of the supplier.</P>
      <P>Exports may qualify for zero-rating if specific conditions are met. Maintaining proper export evidence is critical.</P>
      <p className="text-gray-700 mb-2">Common RCM examples:</p>
      <BulletList items={['Imported services', 'Imported goods', 'Certain electronic devices between registrants']} />

      <H2 id="returns">VAT Returns and Compliance</H2>
      <P>Most UAE businesses file <strong>quarterly VAT returns</strong>. Returns and payments must generally be submitted via <strong>EmaraTax within 28 days</strong> following the end of the tax period.</P>

      <H2 id="mistakes">Common VAT Mistakes</H2>
      <BulletList items={['Late registration', 'Incorrect VAT treatment', 'Missing tax invoices', 'Poor record-keeping', 'Failure to apply reverse charge']} />

      <H2 id="penalties">VAT Penalties</H2>
      <p className="text-gray-700 mb-2">Penalties may arise for:</p>
      <BulletList items={['Late registration', 'Late filing', 'Late payment', 'Incorrect returns', 'Failure to maintain records']} />
      <Callout variant="warning" title="Penalties add up quickly">
        UAE VAT penalties compound on late filings and payments, and can escalate significantly for incorrect returns or failure to maintain proper records. Prevention is always cheaper than remediation.
      </Callout>

      <H2 id="deregistration">VAT Deregistration</H2>
      <P>Businesses may apply for deregistration when taxable supplies cease, or when turnover falls below the mandatory registration threshold.</P>

      <H2 id="wpaa-help">How WPAA Can Help</H2>
      <P>VAT compliance is more than filing returns. Proper planning, documentation and tax recovery can significantly improve cash flow while reducing compliance risk.</P>
      <P>WPAA assists businesses with VAT registration, VAT returns, VAT health checks and FTA compliance support.</P>
    </ArticleShell>
  );
};

export default UAEVATGuide;
